import axios, { AxiosResponse } from 'axios';
import { BASE_URL, VIDEO_ASSET_API_PATH, VIDEO_THUMBNAIL_API_PATH } from 'constants/api';
import { UPLOAD_CHUNK_SIZE } from 'constants/constant';
import { ApiService } from 'service/api/apiService';
import { FetchResult } from 'type/apiType';
import { UploadedPartsInterface, UploadVideoInterface } from 'type/uploadVideoType';
import { VideoThumbnailType } from 'type/videoThumbnailType';
import { VideoType } from 'type/videoType';
import { uploadVideoStore } from 'upload/upload-video/store/uploadVideoStore';

class VideoService extends ApiService<VideoType> {
  public async fetchVideos(
    searchParams?: URLSearchParams
  ): Promise<{ data: VideoType[]; count: number }> {
    const response: FetchResult<VideoType> = await this.fetch(searchParams);
    return {
      data: response.results || [],
      count: response.count || 0,
    };
  }

  public async videoTranscode(
    contentId: number,
    videoId: number,
    subtitleIds: number[] | null,
    serviceName: string,
    assetId: number
  ): Promise<void> {
    await this.createBySubPath('add-subtitle', {
      content_id: contentId,
      video_id: videoId,
      subtitle_ids: subtitleIds,
      service_name: serviceName,
      content_video_asset_id: assetId,
    });
  }

  public async uploadVideoFiles(
    videoFiles: UploadVideoInterface[],
    isContentCreated: boolean = false,
    _autoTranscode: boolean = false,
    _contentId?: number
  ): Promise<void> {
    await Promise.all(
      videoFiles.map(async (videoFile: UploadVideoInterface) => {
        if (!this.validateVideoFile(videoFile)) {
          videoFile.state = 'failed';
          uploadVideoStore.updateUploadVideo(videoFile);
          return;
        }

        const { title, name, type, size } = videoFile;
        const {
          videoId,
          uploadId,
          videoFileName: fileName,
        } = await this.directUploadStart(title || name, type!, size!);

        if (videoId === 0) {
          videoFile.state = 'failed';
          uploadVideoStore.updateUploadVideo(videoFile);
          return;
        }

        // DB 커밋 대기 시간 추가 (Video 객체가 DB에 반영될 시간 확보)
        await new Promise(resolve => setTimeout(resolve, 500));

        try {
          const uploadedParts = await this.uploadVideoFilesWithStreaming(
            videoFile,
            uploadId,
            videoId
          );
          await this.directUploadComplete(
            videoFile,
            videoId,
            uploadId,
            uploadedParts,
            isContentCreated
          );

          videoFile.state = 'uploaded';
          videoFile.percent = 100;
          videoFile.name = fileName;
          videoFile.response = { id: videoId };
          uploadVideoStore.updateUploadVideo(videoFile);
        } catch (error) {
          console.error('Upload error for file:', videoFile.name, error);
          await this.createBySubPath('multipart-upload-failed', {
            video_id: videoId,
          });
          videoFile.state = 'failed';
          uploadVideoStore.updateUploadVideo(videoFile);
          throw error;
        }
      })
    );
  }

  private async directUploadStart(
    fileName: string,
    fileType: string,
    fileSize: number
  ): Promise<{
    videoId: number;
    uploadId: string;
    videoFileName: string;
  }> {
    try {
      return await this.createBySubPath('multipart-upload-start', {
        file_name: fileName,
        file_type: fileType,
        file_size: fileSize,
      });
    } catch (error) {
      console.error('Direct upload start error:', error);
      return {
        videoId: 0,
        uploadId: '',
        videoFileName: '',
      };
    }
  }

  private async fetchPreSignedUrlWithRetry(
    videoFile: UploadVideoInterface,
    uploadId: string,
    videoId: number,
    partNumber: number,
    maxRetries: number = 10
  ): Promise<string> {
    let retryCount = 0;
    let lastError: any = null;

    while (retryCount < maxRetries) {
      try {
        const url = await this.createBySubPath<string>('multipart-upload-pre-signed-url', {
          file_name: videoFile.title || videoFile.name,
          video_id: videoId,
          upload_id: uploadId,
          part_number: partNumber,
        });
        return url;
      } catch (error: any) {
        lastError = error;
        retryCount++;

        // 404 에러 또는 NOTFOUND001 에러인지 확인
        const isNotFoundError =
          error?.response?.status === 404 ||
          error?.response?.data?.code === 'NOTFOUND001' ||
          error?.response?.data?.message?.includes('Resource not found') ||
          error?.message?.includes('404');

        // 404/NOTFOUND001 에러이고 재시도 횟수가 남아있으면 재시도
        if (isNotFoundError && retryCount < maxRetries) {
          // Exponential backoff with jitter: 200ms, 400ms, 800ms, 1600ms, 3200ms, ...
          // 최대 5초까지 증가
          const baseDelay = 200;
          const delayMs = Math.min(
            baseDelay * Math.pow(2, retryCount - 1) + Math.random() * 100,
            5000
          );

          console.log(
            `404/NOTFOUND001 error for part ${partNumber}, retrying in ${Math.round(delayMs)}ms ` +
              `(attempt ${retryCount}/${maxRetries})`
          );
          await new Promise(resolve => setTimeout(resolve, delayMs));
          continue;
        }

        // 재시도 불가능한 에러이거나 최대 재시도 횟수 초과
        if (retryCount >= maxRetries) {
          console.error(
            `Failed to fetch presigned URL for part ${partNumber} after ${retryCount} attempts:`,
            {
              status: error?.response?.status,
              code: error?.response?.data?.code,
              message: error?.response?.data?.message,
              videoId,
              uploadId,
            }
          );
        }
        throw error;
      }
    }

    throw lastError;
  }

  /**
   * 스트리밍 방식으로 업로드: presigned URL을 배치로 받아서 즉시 업로드
   * - Presigned URL 만료 위험 감소
   * - 메모리 효율적
   * - 에러 시 일부만 재시도 가능
   */
  private async uploadVideoFilesWithStreaming(
    videoFile: UploadVideoInterface,
    uploadId: string,
    videoId: number
  ): Promise<UploadedPartsInterface[]> {
    const { size, originFileObj } = videoFile;
    const totalParts = Math.ceil(size! / UPLOAD_CHUNK_SIZE);
    const chunkProgressArray = new Array(totalParts).fill(0);
    const uploadedParts: UploadedPartsInterface[] = [];
    let lastUploadedBytes = 0;
    let lastTime = performance.now();

    // 업로드 시작 시 상태 업데이트
    videoFile.state = 'uploading';
    uploadVideoStore.updateUploadVideo(videoFile);

    const BATCH_SIZE = 50; // 한 번에 처리할 파트 수

    // 배치 단위로 presigned URL 받기 → 업로드 반복
    for (let i = 0; i < totalParts; i += BATCH_SIZE) {
      const batchStart = i;
      const batchEnd = Math.min(i + BATCH_SIZE, totalParts);
      const batchSize = batchEnd - batchStart;

      // 현재 배치의 presigned URL 받기
      const batchPreSignedUrls = await Promise.all(
        Array.from({ length: batchSize }, (_, idx) => {
          const partNumber = batchStart + idx + 1;
          return this.fetchPreSignedUrlWithRetry(videoFile, uploadId, videoId, partNumber);
        })
      );

      // 받은 presigned URL로 즉시 업로드
      await Promise.all(
        batchPreSignedUrls.map(async (preSignedUrl, batchIdx) => {
          const partNumber = batchStart + batchIdx;
          const start = partNumber * UPLOAD_CHUNK_SIZE;
          const end = Math.min((partNumber + 1) * UPLOAD_CHUNK_SIZE, size!);
          const chunk = originFileObj?.slice(start, end);

          const preSignedUrlResponse = await this.putPreSignedUrlWithRetry({
            preSignedUrl,
            chunk: chunk as Blob,
            partNumber: partNumber + 1,
            onChunkProgress: progress => {
              chunkProgressArray[partNumber] = progress;
              const uploadedBytes = chunkProgressArray.reduce((acc, val) => acc + val, 0);
              const overallProgress = (uploadedBytes / size!) * 100;
              const currentTime = performance.now();
              const elapsedSeconds = (currentTime - lastTime) / 1000;

              const bytesUploadedSinceLastCheck = uploadedBytes - lastUploadedBytes;
              const uploadSpeedBytesPerSecond =
                elapsedSeconds > 0 ? bytesUploadedSinceLastCheck / elapsedSeconds : 0;

              // uploadVideoStore를 통한 진행률 업데이트
              uploadVideoStore.updateProgress(
                videoFile.uid,
                overallProgress,
                uploadSpeedBytesPerSecond
              );

              lastUploadedBytes = uploadedBytes;
              lastTime = currentTime;
            },
          });

          uploadedParts.push({
            ETag: preSignedUrlResponse.headers.etag,
            PartNumber: partNumber + 1,
          });
        })
      );
    }

    return uploadedParts;
  }

  private async directUploadComplete(
    videoFile: UploadVideoInterface,
    videoId: number,
    uploadId: string,
    uploadedParts: UploadedPartsInterface[],
    isContentCreated: boolean = false
  ): Promise<void> {
    const { title, name } = videoFile;
    await this.createBySubPath('multipart-upload-complete', {
      file_name: title || name,
      video_id: videoId,
      upload_id: uploadId,
      parts: uploadedParts,
      is_content_created: isContentCreated,
    });
  }

  private validateVideoFile(videoFile: UploadVideoInterface): boolean {
    const { size, name, type } = videoFile;
    return !!size && !!name && !!type;
  }

  /**
   * S3 업로드 재시도 로직 포함
   */
  private async putPreSignedUrlWithRetry(putPreSignedUrlProps: {
    preSignedUrl: string;
    chunk: Blob;
    partNumber: number;
    onChunkProgress: (progress: number) => void;
    maxRetries?: number;
  }): Promise<AxiosResponse<any>> {
    const {
      preSignedUrl,
      chunk,
      partNumber,
      onChunkProgress,
      maxRetries = 3,
    } = putPreSignedUrlProps;
    let retryCount = 0;
    let lastError: any = null;

    while (retryCount < maxRetries) {
      try {
        return await axios.put(preSignedUrl, chunk, {
          headers: {
            'Content-Type': chunk.type,
          },
          onUploadProgress: (progressEvent): void => {
            onChunkProgress(progressEvent.loaded);
          },
        });
      } catch (error: any) {
        lastError = error;
        retryCount++;

        // 재시도 가능한 에러인지 확인
        const isRetryableError =
          error?.code === 'ERR_NETWORK' ||
          error?.code === 'ECONNABORTED' ||
          error?.response?.status >= 500 ||
          error?.response?.status === 0 ||
          error?.message?.includes('timeout') ||
          error?.message?.includes('CORS');

        if (isRetryableError && retryCount < maxRetries) {
          const delayMs = Math.min(1000 * Math.pow(2, retryCount - 1), 5000);
          console.log(
            `Upload failed for part ${partNumber}, retrying in ${delayMs}ms (attempt ${retryCount}/${maxRetries})`
          );
          await new Promise(resolve => setTimeout(resolve, delayMs));
          continue;
        }

        // 재시도 불가능하거나 최대 재시도 횟수 초과
        console.error(`Failed to upload part ${partNumber} after ${retryCount} attempts:`, error);
        throw error;
      }
    }

    throw lastError;
  }

  async videoAssetTranscode(videoId: number): Promise<void> {
    await this.createBySubPath('transcoding', {
      video_id: videoId,
    });
  }

  async uploadThumbnail(
    videoId: number,
    imageBlob: Blob,
    isDefault: boolean = true,
    language: string | null = null
  ): Promise<VideoThumbnailType> {
    const formData = new FormData();
    formData.append('video', videoId.toString());
    formData.append('image', imageBlob, 'thumbnail.jpg');
    formData.append('is_default', isDefault.toString());
    if (language) {
      formData.append('language', language);
    }

    const response = await axios.post<VideoThumbnailType>(
      `${BASE_URL}${VIDEO_THUMBNAIL_API_PATH}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  }

  async updateThumbnail(
    thumbnailId: number,
    imageBlob?: Blob,
    isDefault?: boolean,
    language?: string | null
  ): Promise<VideoThumbnailType> {
    const formData = new FormData();
    if (imageBlob) {
      formData.append('image', imageBlob, 'thumbnail.jpg');
    }
    if (isDefault !== undefined) {
      formData.append('is_default', isDefault.toString());
    }
    if (language !== undefined) {
      formData.append('language', language || '');
    }

    const response = await axios.patch<VideoThumbnailType>(
      `${BASE_URL}${VIDEO_THUMBNAIL_API_PATH}${thumbnailId}/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  }

  async deleteThumbnail(thumbnailId: number): Promise<void> {
    await axios.delete(`${BASE_URL}${VIDEO_THUMBNAIL_API_PATH}${thumbnailId}/`);
  }

  async submitSampleVideo(videoId: number): Promise<void> {
    await this.createBySubPath(`${videoId}/submit-sample-video`, {});
  }

  async verifyVideoFiles(videoId: number): Promise<void> {
    await this.createBySubPath(`${videoId}/verify-video-files/`, {});
  }
}

export const videoService = new VideoService(VIDEO_ASSET_API_PATH);
